import type { ThemeMode } from './types';

/**
 * Every --xpt-* var name. Adding a var = add here + update ALL entries in CELL_COLOR_VARS below.
 * TypeScript enforces completeness via Record<ThemeMode, XptCellVars>.
 */
export type XptCellVarName =
  | '--xpt-cell-editable-bg'
  | '--xpt-cell-error-bg'
  | '--xpt-cell-error-fg'
  | '--xpt-cell-error-row-bg'
  | '--xpt-cell-warning-bg'
  | '--xpt-cell-warning-fg'
  | '--xpt-cell-modified-bg'
  | '--xpt-cell-readonly-bg'
  | '--xpt-cell-historical-bg'
  | '--xpt-cell-historical-total-bg'
  | '--xpt-cell-projected-bg'
  | '--xpt-cell-projected-total-bg'
  | '--xpt-cell-grand-total-bg'
  | '--xpt-cell-quarterly-total-bg'
  | '--xpt-cell-actual-bg'
  | '--xpt-variance-positive-fg'
  | '--xpt-variance-negative-fg'
  | '--xpt-variance-neutral-fg'
  | '--xpt-variance-positive-bg'
  | '--xpt-variance-negative-bg'
  | '--xpt-header-accent-primary'
  | '--xpt-header-accent-secondary';

export type XptCellVars = Record<XptCellVarName, string>;

/** Workbook-only colors that are not emitted as theme variables. */
export const EXPORT_ONLY_COLORS = {
  greyBg: '#F2F2F2',
  editableBg: '#FFFFED'
} as const;

/**
 * Per-mode color values — the ONLY file in the codebase that contains color hex values.
 * TS guarantees every mode has every var.
 *
 * NOTE: `--xpt-variance-neutral-fg` holds a `var()` reference, not a resolved hex, so it is
 * CSS-only and must not be used anywhere a literal color is required (e.g. Excel export styles).
 */
export const CELL_COLOR_VARS: Record<ThemeMode, XptCellVars> = {
  light: {
    '--xpt-cell-editable-bg': '#fffdf5',
    '--xpt-cell-error-bg': '#fbc9cd',
    '--xpt-cell-error-fg': '#d13212',
    '--xpt-cell-error-row-bg': '#fdeced',
    '--xpt-cell-warning-bg': '#fffbe5',
    '--xpt-cell-warning-fg': '#8a6500',
    '--xpt-cell-modified-bg': '#e7f3fd',
    '--xpt-cell-readonly-bg': '#f8f8f8',
    '--xpt-cell-historical-bg': '#f0f4f8',
    '--xpt-cell-historical-total-bg': '#e0e8ef',
    '--xpt-cell-projected-bg': '#f5f0ff',
    '--xpt-cell-projected-total-bg': '#e8dfff',
    '--xpt-cell-grand-total-bg': '#ffe9b3',
    '--xpt-cell-quarterly-total-bg': '#ebeae8',
    '--xpt-cell-actual-bg': '#bab9b6',
    '--xpt-variance-positive-fg': '#037f0c',
    '--xpt-variance-negative-fg': '#d13212',
    '--xpt-variance-neutral-fg': 'var(--ag-foreground-color)',
    '--xpt-variance-positive-bg': '#037f0c',
    '--xpt-variance-negative-bg': '#d13212',
    '--xpt-header-accent-primary': '#0073bb',
    '--xpt-header-accent-secondary': '#879596'
  },
  dark: {
    // Subtle dark-yellow tint (not blue-gray) so editable cells read as yellow in dark mode,
    // mirroring the light-mode pale-yellow affordance. Kept pale/desaturated vs. the warning bg.
    '--xpt-cell-editable-bg': '#34311d',
    '--xpt-cell-error-bg': '#6a2323',
    '--xpt-cell-error-fg': '#ff8a80',
    '--xpt-cell-error-row-bg': '#3a1717',
    '--xpt-cell-warning-bg': '#3d3520',
    '--xpt-cell-warning-fg': '#ffe082',
    '--xpt-cell-modified-bg': '#1a2a3f',
    '--xpt-cell-readonly-bg': '#1e1e28',
    '--xpt-cell-historical-bg': '#1a2030',
    '--xpt-cell-historical-total-bg': '#252b40',
    '--xpt-cell-projected-bg': '#261a3f',
    '--xpt-cell-projected-total-bg': '#3a2a5f',
    '--xpt-cell-grand-total-bg': '#3a2d1a',
    '--xpt-cell-quarterly-total-bg': '#2a2a35',
    '--xpt-cell-actual-bg': '#35353f',
    '--xpt-variance-positive-fg': '#7dd87f',
    '--xpt-variance-negative-fg': '#ff8a80',
    '--xpt-variance-neutral-fg': 'var(--ag-foreground-color)',
    '--xpt-variance-positive-bg': '#2e7d32',
    '--xpt-variance-negative-bg': '#c62828',
    '--xpt-header-accent-primary': '#539fe5',
    '--xpt-header-accent-secondary': '#a9b4b7'
  }
};
